<template>
	<slideover :open="show" title="Review Document" @close="emit('close')">
		<div class="p-4 space-y-4">
			<app-alert theme="info" spacing="mb-0">
				<div>
					<p class="font-semibold">{{ document?.name }}</p>
					<p class="text-xs">
						Revision {{ revision?.revision_no }} &mdash; please approve or reject
						this document.
					</p>
				</div>
			</app-alert>

			<app-input
				v-model="form.review_comments"
				label="Comments (optional)"
				type="textarea"
				placeholder="Add an optional comment"
				:error="errors.review_comments" />
		</div>

		<template #action>
			<div class="w-full flex justify-end space-x-3">
				<app-button :loading="loading" theme="danger" @click="submit('rejected')">
					Reject
				</app-button>
				<app-button :loading="loading" theme="success" @click="submit('approved')">
					Approve
				</app-button>
			</div>
		</template>
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, watch } from 'vue'
	import Slideover from '@/components/partials/Slideover.vue'
	import AppButton from '@/components/base/AppButton.vue'
	import AppInput from '@/components/base/forms/AppInput.vue'
	import AppAlert from '@/components/base/AppAlert.vue'
	import DocumentService from '@/services/document'
	import useToasterStore from '@/stores/toaster'
	import type { Document } from '@/data/schema/document'
	import type { DocumentRevision } from '@/data/schema/document-revision'

	const props = defineProps<{
		show: boolean
		document: Document | null
		revision: DocumentRevision | null
	}>()

	const emit = defineEmits(['close', 'reviewed'])

	const toaster = useToasterStore()

	const form = ref<{ review_comments: string }>({
		review_comments: ''
	})
	const errors = ref<any>({})
	const loading = ref(false)

	watch(
		() => props.show,
		(open) => {
			if (open) {
				form.value.review_comments = ''
				errors.value = {}
			}
		}
	)

	const submit = async (status: 'approved' | 'rejected') => {
		if (!props.document || !props.revision) return

		errors.value = {}
		loading.value = true

		const documentService = new DocumentService()
		try {
			await documentService.reviewRevision(props.document.id, props.revision.id, {
				review_status: status,
				review_comments: form.value.review_comments || null
			})
			toaster.success({
				text: status === 'approved' ? 'Document approved.' : 'Document rejected.'
			})
			emit('reviewed')
			emit('close')
		} catch (error) {
			if (error.response?.data?.errors) {
				errors.value = error.response.data.errors
			}
			toaster.error({ text: 'Could not submit review. Please try again.' })
			console.error('Error reviewing document:', error)
		} finally {
			loading.value = false
		}
	}
</script>
